import React, { useState, useEffect } from 'react';
import { X, Download, Share, MoreVertical } from 'lucide-react';
import { Button } from '../ui/Button';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Detect iOS
const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

// Detect Chrome on iOS
const isChromeiOS = (): boolean => {
  return isIOS() && /CriOS/.test(navigator.userAgent);
};

// Detect if running as standalone PWA
const isStandalone = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true;
};

export const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (isStandalone()) return;

    // Check if user dismissed the banner recently (24h)
    const dismissedAt = localStorage.getItem('pwa_install_dismissed');
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
      if (dismissedTime > oneDayAgo) return;
    }

    // For iOS, show instructions after a delay
    if (isIOS()) {
      const timer = setTimeout(() => {
        setShowIOSInstructions(true);
        setShowBanner(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    // For other browsers, listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app was installed
    window.addEventListener('appinstalled', () => {
      setShowBanner(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-slideUp">
      <div className="bg-bg-card border border-gray-700 rounded-xl p-4 shadow-xl">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-text-muted hover:text-text-primary"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary-orange/20 flex items-center justify-center flex-shrink-0">
            <img src="/logo-512.png" alt="Ball M" className="w-8 h-8 rounded-lg" />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-text-primary mb-1">
              Installer Ball M
            </h3>

            {showIOSInstructions ? (
              <div className="text-sm text-text-secondary">
                {isChromeiOS() ? (
                  <>
                    <p className="mb-2">Pour installer depuis Chrome :</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>Appuyez sur <MoreVertical className="w-4 h-4 inline mx-1" /> (3 points en haut à droite)</li>
                      <li>Appuyez sur <strong>"Ajouter à l'écran d'accueil"</strong></li>
                      <li>Confirmez en appuyant sur <strong>"Ajouter"</strong></li>
                    </ol>
                    <p className="mt-2 text-xs text-text-muted">
                      💡 Astuce : Ouvrez ce site dans Safari pour une meilleure expérience PWA
                    </p>
                  </>
                ) : (
                  <>
                    <p className="mb-2">Pour installer l'app :</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>Appuyez sur <Share className="w-4 h-4 inline mx-1" /> (en bas de l'écran)</li>
                      <li>Faites défiler et appuyez sur <strong>"Sur l'écran d'accueil"</strong></li>
                      <li>Appuyez sur <strong>"Ajouter"</strong></li>
                    </ol>
                  </>
                )}
              </div>
            ) : (
              <>
                <p className="text-sm text-text-secondary mb-3">
                  Accédez rapidement à vos commandes depuis votre écran d'accueil
                </p>
                <Button
                  size="sm"
                  onClick={handleInstall}
                  leftIcon={<Download className="w-4 h-4" />}
                >
                  Installer
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
